import React from 'react';
import { injectIntl } from 'react-intl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { get } from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { ReactComponent as CloseIcon } from '../../assets/icons/close-regular.svg';
import { confirmSpecialOfferPayment } from '../../ducks/Transaction.duck';
import { ensureTransaction } from '../../util/data';

import InboxDetailsManager from './InboxDetailsManager';
import InboxStripePayment from './InboxStripePayment';
import InboxReview from './InboxReview';

import css from './InboxDetails.module.css';
import { getListingImages, getPackageImages } from '../../util/listing';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import TripInsuranceBadges from '../TripInsuranceBadges/TripInsuranceBadges';
import { fetchCurrentUser, followSubject } from '../../ducks/user.duck';
import { getPackageFromTransaction } from '../../util/transaction';

export const InboxDetails = ({
  history,
  transaction,
  params,
  currentUser,
  processTransitions,
  checkUserVerification,
  isFetching,
  transactionState,
  currentListing,
  isMobile,
  onChangeBookingDetails,
  followSubjectMutation,
  fetchUser,
  onConfirmSpecialOfferPayment,
  onRefetchTransaction,
}) => {
  if (!transaction) {
    return null;
  }

  const currentTransaction = ensureTransaction(transaction);

  const contentClassName = classNames(css.content, {
    [css.contentLoading]: isFetching,
  });

  const packageId =
    get(currentTransaction, 'attributes.protectedData.selectedPackage.packageId', null) ||
    get(currentTransaction, 'attributes.protectedData.packageLineItem.id', null);

  const currentPackage = getPackageFromTransaction(currentListing, packageId, currentTransaction);

  const packagesImages = currentPackage
    ? getPackageImages(currentListing, currentPackage?.id)
    : null;
  const listingImage = getListingImages(currentListing)[0] || null;
  const firstImage = packagesImages && packagesImages.length ? packagesImages[0] : listingImage;

  const onClose = () => {
    history.push(
      createResourceLocatorString('InboxV2Page', routeConfiguration(), {
        tab: params.tab,
      })
    );
  };

  const handleConfirmSpecialOfferPayment = async confirmParams => {
    await onConfirmSpecialOfferPayment(confirmParams);
    await onRefetchTransaction(currentTransaction.id.uuid);
  };

  if (isFetching) {
    return (
      <div className={css.root}>
        <div className={css.headerContainerLoading}>
          <div className={contentClassName}>
            <div className={css.loaderContainer}>
              <Skeleton height="100%" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Build srcSet for responsive image loading - browser picks best size
  const imageVariants = firstImage?.attributes?.variants;
  const imageSrcSet = imageVariants
    ? ['scaled-small', 'scaled-medium', 'scaled-large', 'landscape-crop']
        .map(variant => {
          const v = imageVariants[variant];
          return v && v.url && v.width ? `${v.url} ${v.width}w` : null;
        })
        .filter(Boolean)
        .join(', ')
    : null;
  const imageUrl = imageVariants?.['landscape-crop']?.url || imageVariants?.['scaled-medium']?.url;

  return (
    <div className={classNames(css.root)}>
      <div className={css.headerContainer}>
        {imageUrl && (
          <img
            src={imageUrl}
            srcSet={imageSrcSet}
            sizes="(min-width: 768px) 30vw, 100vw"
            alt=""
            className={css.headerBackgroundImage}
            fetchpriority="high"
            loading="eager"
            decoding="async"
          />
        )}
        {isMobile && (
          <button type="button" className={css.buttonClose} onClick={onClose}>
            <CloseIcon />
          </button>
        )}

        {currentTransaction && <TripInsuranceBadges currentTransaction={currentTransaction} />}

        <div className={contentClassName}>
          <InboxDetailsManager
            history={history}
            transaction={transaction}
            currentUser={currentUser}
            params={params}
            processTransitions={processTransitions}
            checkUserVerification={checkUserVerification}
            onChangeBookingDetails={onChangeBookingDetails}
            onRefetchTransaction={onRefetchTransaction}
          />
        </div>
      </div>

      <div className={css.footer}>
        <InboxReview currentUser={currentUser} currentTransaction={currentTransaction} />

        <InboxStripePayment
          currentTransaction={currentTransaction}
          currentUser={currentUser}
          currentListing={currentListing}
          transactionState={transactionState}
          followSubjectMutation={followSubjectMutation}
          fetchUser={fetchUser}
          onConfirmSpecialOfferPayment={handleConfirmSpecialOfferPayment}
        />
      </div>
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  followSubjectMutation: (userId, subjectId, notifyBy) =>
    dispatch(followSubject(userId, subjectId, notifyBy)),
  fetchUser: () => dispatch(fetchCurrentUser()),
  onConfirmSpecialOfferPayment: transactionId =>
    dispatch(confirmSpecialOfferPayment(transactionId)),
});

export default compose(
  connect(
    null,
    mapDispatchToProps
  )
)(injectIntl(InboxDetails));
